import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { API_IHO_URL } from '../../../app.module';
import { CacheService } from '../cache.service';

@Injectable()
export class BaseRepository {
 protected http: HttpClient;
 protected baseUrl: string;
 protected cacheService: CacheService;

 protected options_: any = {
  observe: 'response',
  responseType: 'json',
  // ca: cert,
  headers: new HttpHeaders({
   Accept: 'application/json',
  }),
  withCredentials: false,
 };

 constructor(
  @Inject(HttpClient) http: HttpClient,
  @Inject(CacheService) cacheService: CacheService,
  @Inject(PLATFORM_ID) public platformId: Object,
  @Optional() @Inject(API_IHO_URL) baseUrl?: string
 ) {
  this.http = http;
  this.baseUrl = baseUrl ?? '';
  this.cacheService = cacheService;
 }

 public getWithoutCache<T>(url: string, options = this.options_): Observable<T> {
  return this.http.get<T>(url, options).pipe(
   catchError((err) => {
    throw err;
   }),
   map((response: HttpEvent<T>) => {
    if (response instanceof HttpResponse) {
     return response.body as T;
    }
    throw new Error('Unexpected response type');
   })
  );
 }

 public get<T>(url: string, mandatoryFetch = false, options = this.options_): Observable<T> {
  if (mandatoryFetch) {
   this.cacheService.delete(url);
   return this.sendGetRequest(url, options);
  }

  const cachedResponse = this.cacheService.get(url);
  if (cachedResponse) {
   return cachedResponse as Observable<T>;
  }
  return this.sendGetRequest(url, options);
 }

 public post<T>(url: string, data: any, options = this.options_): Observable<T> {
  return this.http.post<T>(url, data, options).pipe(
   catchError((err) => {
    throw err;
   }),
   map((response: HttpEvent<T>) => {
    if (response instanceof HttpResponse) {
     return response.body as T;
    }
    throw new Error('Unexpected response type');
   })
  );
 }

 public put<T>(url: string, data: any, options = this.options_): Observable<T> {
  return this.http.put<T>(url, data, options).pipe(
   catchError((err) => {
    throw err;
   }),
   map((response: HttpEvent<T>) => {
    if (response instanceof HttpResponse) {
     return response.body as T;
    }
    throw new Error('Unexpected response type');
   })
  );
 }

 public delete<T>(url: string, options = this.options_): Observable<T> {
  return this.http.delete<T>(url, options).pipe(
   catchError((err) => {
    throw err;
   }),
   map((response: HttpEvent<T>) => {
    if (response instanceof HttpResponse) {
     return response.body as T;
    }
    throw new Error('Unexpected response type');
   })
  );
 }

 private sendGetRequest<T>(url: string, options = this.options_): Observable<T> {
  return this.http.get<T>(url, options).pipe(
   catchError((err) => {
    throw err;
   }),
   map((response: HttpEvent<T>) => {
    if (response instanceof HttpResponse) {
     this.cacheService.put(url, of(response.body));
     return response.body as T;
    }
    throw new Error('Unexpected response type');
   })
  );
 }
}
